import React, { useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Stack,
  Typography,
  Switch,
  Chip,
  Tooltip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import DateRangeIcon from '@mui/icons-material/DateRange';
import BoltIcon from '@mui/icons-material/Bolt';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { formatDuration, formatDateUTC, minWindowSeconds, formatCount } from './studioUtils';

/**
 * Time window picker — a single interactive "data map".
 *
 * Everything about the window lives in ONE widget instead of being scattered
 * across a slider + pickers + presets + a separate distribution chart:
 *
 *   - The backdrop is a per-signal density timeline (real signal counts per
 *     time bucket) spanning the whole workspace history, so you can SEE where
 *     your data is dense, sparse, or missing.
 *   - A draggable selection window sits directly on top. Drag its edges to
 *     set start / end, or drag the body to slide it. The area outside the
 *     selection is dimmed, so "what you're training on" is unmistakable.
 *   - From / To date-time fields and preset chips are precise / quick
 *     alternates; they stay in sync with the brush.
 *   - Live pins the right edge to "now" and the window rolls with new data.
 *
 * A created segment persists either an explicit [window_start, window_end]
 * (historical) or tail_seconds + live (rolling), which the parent derives
 * from the bounds this widget reports via onChangeTail / onChangeEnd.
 */
const PRESETS = [
  { label: 'All', seconds: null },
  { label: '1y', seconds: 31536000 },
  { label: '6mo', seconds: 15552000 },
  { label: '90d', seconds: 7776000 },
  { label: '30d', seconds: 2592000 },
  { label: '7d', seconds: 604800 },
  { label: '24h', seconds: 86400 },
];

const LABEL_W = 96;
const COUNT_W = 46;
const ROW_H = 20;

const RangePicker = ({
  workspaceStartMs,
  workspaceEndMs,
  tailSeconds,
  onChangeTail,
  live,
  onToggleLive,
  interval,
  tracks = [],
  endMs = null,
  onChangeEnd,
}) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const trackRef = useRef(null);
  const dragRef = useRef(null);

  const hasSpan =
    workspaceStartMs != null && workspaceEndMs != null && workspaceEndMs > workspaceStartMs;
  const span = hasSpan ? workspaceEndMs - workspaceStartMs : 0;
  const fullSpanSec = hasSpan ? span / 1000 : tailSeconds || 1;
  const minWin = Math.min(minWindowSeconds(interval), fullSpanSec);

  const latestMs = workspaceEndMs != null ? workspaceEndMs : Date.now();
  const clampedTail = Math.max(minWin, Math.min(tailSeconds || fullSpanSec, fullSpanSec));

  // Visual window over the DATA axis. For live we anchor the right edge to
  // the latest signal (data only exists up to there); the "now" rolling is
  // communicated by the Live pill + To field.
  const visEndMs = live ? latestMs : endMs != null ? endMs : latestMs;
  const visStartMs = visEndMs - clampedTail * 1000;

  const toFrac = (ms) =>
    hasSpan ? Math.max(0, Math.min(1, (ms - workspaceStartMs) / span)) : 0;
  const selStartFrac = toFrac(visStartMs);
  const selEndFrac = toFrac(visEndMs);

  const visibleTracks = tracks.slice(0, 12);

  // ---- The single mutator everything funnels through -------------------
  const setWindowByBounds = (startMs, endMsRaw, opts = {}) => {
    if (!hasSpan) return;
    let s = Math.max(workspaceStartMs, Math.min(startMs, workspaceEndMs));
    let e = Math.max(workspaceStartMs, Math.min(endMsRaw, workspaceEndMs));
    if (e - s < minWin * 1000) {
      if (opts.fromRight) s = Math.max(workspaceStartMs, e - minWin * 1000);
      else e = Math.min(workspaceEndMs, s + minWin * 1000);
    }
    const tail = Math.max(minWin, Math.round((e - s) / 1000));
    const eps = span * 0.004;
    const anchorLatest = e >= workspaceEndMs - eps;
    if (live && !anchorLatest && onToggleLive) onToggleLive(false);
    if (onChangeEnd) onChangeEnd(anchorLatest ? null : e);
    if (onChangeTail) onChangeTail(tail);
  };

  // ---- Pointer dragging -------------------------------------------------
  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const msAtX = workspaceStartMs + frac * span;
    if (d.type === 'left') {
      setWindowByBounds(msAtX, d.origEnd);
    } else if (d.type === 'right') {
      setWindowByBounds(d.origStart, msAtX, { fromRight: true });
    } else {
      const deltaMs = ((e.clientX - d.startX) / rect.width) * span;
      const len = d.origEnd - d.origStart;
      let s = Math.max(workspaceStartMs, Math.min(d.origStart + deltaMs, workspaceEndMs - len));
      setWindowByBounds(s, s + len);
    }
  };
  const endDrag = () => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', endDrag);
    document.body.style.userSelect = '';
  };
  const beginDrag = (type) => (e) => {
    if (!hasSpan) return;
    if (type === 'right' && live) return; // right edge is "now" while live
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { type, startX: e.clientX, origStart: visStartMs, origEnd: visEndMs };
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', endDrag);
  };
  // Remove any lingering drag listeners on unmount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => endDrag, []);

  // ---- Date pickers -----------------------------------------------------
  const handleFromChange = (date) => {
    if (!date || Number.isNaN(date.getTime())) return;
    setWindowByBounds(date.getTime(), visEndMs);
  };
  const handleToChange = (date) => {
    if (!date || Number.isNaN(date.getTime())) return;
    setWindowByBounds(visStartMs, date.getTime(), { fromRight: true });
  };

  // ---- Presets ----------------------------------------------------------
  const availablePresets = useMemo(
    () => PRESETS.filter((p) => p.seconds == null || p.seconds <= fullSpanSec + 1),
    [fullSpanSec],
  );
  const isActivePreset = (p) => {
    if (p.seconds == null) return clampedTail >= fullSpanSec - 1;
    return Math.abs(clampedTail - p.seconds) / p.seconds < 0.02;
  };
  const applyPreset = (p) => {
    if (onChangeEnd) onChangeEnd(null); // anchor to latest
    const sec = p.seconds == null ? Math.round(fullSpanSec) : Math.min(p.seconds, Math.round(fullSpanSec));
    if (onChangeTail) onChangeTail(Math.max(minWin, sec));
  };

  const scrim = dark ? 'rgba(10,12,10,0.62)' : 'rgba(255,255,255,0.6)';
  const handleColor = theme.palette.primary.main;

  return (
    <Box>
      {/* Header + Live */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <DateRangeIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Time window
          </Typography>
        </Stack>
        <Tooltip title="Live: the window rolls forward to include new data as it arrives.">
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              px: 1,
              borderRadius: 5,
              border: `1px solid ${live ? theme.palette.primary.main : theme.palette.divider}`,
              bgcolor: live ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
            }}
          >
            <BoltIcon sx={{ fontSize: 15, color: live ? 'primary.main' : 'text.disabled' }} />
            <Typography variant="caption" fontWeight={700} color={live ? 'primary.main' : 'text.secondary'}>
              Live
            </Typography>
            <Switch
              size="small"
              checked={!!live}
              onChange={(e) => {
                onToggleLive && onToggleLive(e.target.checked);
                if (e.target.checked && onChangeEnd) onChangeEnd(null);
              }}
            />
          </Stack>
        </Tooltip>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Drag the highlighted window (or its edges) across your data, or set exact
        dates below. Bars show how many points each signal has across time.
      </Typography>

      {/* Floating edge-date labels, aligned to the bucket track */}
      {hasSpan && (
        <Box
          sx={{
            position: 'relative',
            height: 16,
            ml: `${LABEL_W + 8}px`,
            mr: `${COUNT_W + 8}px`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              left: `${selStartFrac * 100}%`,
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              fontSize: '0.64rem',
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            {formatDateUTC(visStartMs)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              left: `${selEndFrac * 100}%`,
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              fontSize: '0.64rem',
              fontWeight: 700,
              color: live ? 'success.main' : 'primary.main',
            }}
          >
            {live ? 'now' : formatDateUTC(visEndMs)}
          </Typography>
        </Box>
      )}

      {/* ---- The data map / brush ---- */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
        {/* Signal labels */}
        <Stack spacing={0.5} sx={{ width: LABEL_W, flexShrink: 0 }}>
          {visibleTracks.length === 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ height: ROW_H, lineHeight: `${ROW_H}px` }}>
              no signals
            </Typography>
          )}
          {visibleTracks.map((t) => (
            <Stack key={t.key} direction="row" alignItems="center" spacing={0.5} sx={{ height: ROW_H, overflow: 'hidden' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.color, flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{ fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {t.label}
              </Typography>
            </Stack>
          ))}
        </Stack>

        {/* Brush track (density backdrop + selection) */}
        <Box
          ref={trackRef}
          sx={{
            position: 'relative',
            flex: 1,
            borderRadius: 1.5,
            p: 0,
            bgcolor: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            cursor: hasSpan ? 'crosshair' : 'default',
          }}
        >
          {/* Density rows */}
          <Stack spacing={0.5} sx={{ p: 0.75 }}>
            {visibleTracks.length === 0 && (
              <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', height: 2, bgcolor: alpha(theme.palette.text.primary, 0.12) }} />
              </Box>
            )}
            {visibleTracks.map((t) => (
              <Tooltip
                key={t.key}
                placement="top"
                title={`${t.label} · ${t.cadenceLabel}${t.totalCount ? ` · ${t.totalCount.toLocaleString()} points` : ''}`}
              >
                <Box sx={{ height: ROW_H, display: 'flex', alignItems: 'stretch', gap: '1.5px' }}>
                  {t.histogram && t.histogram.length > 0 ? (
                    t.histogram.map((b, i) => (
                      <Box
                        key={i}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          position: 'relative',
                          bgcolor: alpha(theme.palette.text.primary, 0.05),
                          borderRadius: '1.5px',
                          overflow: 'hidden',
                        }}
                      >
                        {b.count > 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: 0,
                              height: `${Math.max(22, Math.round(b.intensity * 100))}%`,
                              bgcolor: alpha(t.color, 0.45 + 0.55 * b.intensity),
                            }}
                          />
                        )}
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ position: 'relative', width: '100%', alignSelf: 'center', height: '55%' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: `${(t.startFrac || 0) * 100}%`,
                          width: `${Math.max(2, ((t.endFrac || 1) - (t.startFrac || 0)) * 100)}%`,
                          height: '100%',
                          borderRadius: 4,
                          background: `linear-gradient(90deg, ${alpha(t.color, 0.5)}, ${t.color})`,
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Tooltip>
            ))}
          </Stack>

          {/* Dim scrims outside the selection */}
          {hasSpan && (
            <>
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${selStartFrac * 100}%`, bgcolor: scrim, pointerEvents: 'none' }} />
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: `${(1 - selEndFrac) * 100}%`, bgcolor: scrim, pointerEvents: 'none' }} />

              {/* Selection band (draggable body) */}
              <Box
                onPointerDown={beginDrag('body')}
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${selStartFrac * 100}%`,
                  width: `${Math.max(0.5, (selEndFrac - selStartFrac) * 100)}%`,
                  border: `2px solid ${handleColor}`,
                  borderRadius: 1,
                  cursor: 'grab',
                  bgcolor: alpha(handleColor, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 120ms ease',
                  '&:hover': { bgcolor: alpha(handleColor, 0.18) },
                  '&:active': { cursor: 'grabbing' },
                }}
              >
                {/* Center "drag" affordance */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.25}
                  sx={{
                    pointerEvents: 'none',
                    px: 0.6,
                    py: 0.1,
                    borderRadius: 5,
                    bgcolor: alpha(handleColor, 0.9),
                    color: theme.palette.getContrastText(handleColor),
                    opacity: (selEndFrac - selStartFrac) > 0.08 ? 0.92 : 0,
                  }}
                >
                  <SwapHorizIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 800, lineHeight: 1 }}>
                    DRAG
                  </Typography>
                </Stack>
              </Box>

              {/* Left handle */}
              <Box
                onPointerDown={beginDrag('left')}
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${selStartFrac * 100}%`,
                  width: 16,
                  transform: 'translateX(-50%)',
                  cursor: 'ew-resize',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                  '&:hover .grip': { transform: 'scaleY(1.12)', filter: 'brightness(1.1)' },
                }}
              >
                <Box
                  className="grip"
                  sx={{
                    width: 9,
                    height: '78%',
                    borderRadius: 2,
                    bgcolor: handleColor,
                    boxShadow: theme.palette.boxShadow,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px',
                    transition: 'transform 120ms ease',
                  }}
                >
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: theme.palette.getContrastText(handleColor) }} />
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: theme.palette.getContrastText(handleColor) }} />
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: theme.palette.getContrastText(handleColor) }} />
                </Box>
              </Box>

              {/* Right handle (or Live marker) */}
              <Box
                onPointerDown={beginDrag('right')}
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${selEndFrac * 100}%`,
                  width: 16,
                  transform: 'translateX(-50%)',
                  cursor: live ? 'default' : 'ew-resize',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                  '&:hover .grip': { transform: live ? 'none' : 'scaleY(1.12)', filter: 'brightness(1.1)' },
                }}
              >
                <Box
                  className="grip"
                  sx={{
                    width: 9,
                    height: '78%',
                    borderRadius: 2,
                    bgcolor: live ? theme.palette.success.main : handleColor,
                    boxShadow: live ? `0 0 8px ${theme.palette.success.main}` : theme.palette.boxShadow,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px',
                    transition: 'transform 120ms ease',
                  }}
                >
                  {!live && (
                    <>
                      <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: theme.palette.getContrastText(handleColor) }} />
                      <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: theme.palette.getContrastText(handleColor) }} />
                      <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: theme.palette.getContrastText(handleColor) }} />
                    </>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* Per-signal counts */}
        <Stack spacing={0.5} sx={{ width: COUNT_W, flexShrink: 0 }}>
          {visibleTracks.map((t) => (
            <Box key={t.key} sx={{ height: ROW_H, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Tooltip title={`${t.totalCount ? t.totalCount.toLocaleString() : 0} data points`}>
                <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary' }}>
                  {t.totalCount ? formatCount(t.totalCount) : '·'}
                </Typography>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Axis under the track */}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5, pl: `${LABEL_W + 8}px`, pr: `${COUNT_W + 8}px` }}>
        <Typography variant="caption" color="text.secondary">
          {hasSpan ? formatDateUTC(workspaceStartMs) : 'start'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {hasSpan ? formatDateUTC(workspaceEndMs) : 'latest'}
        </Typography>
      </Stack>

      {/* Exact dates + window length */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <DateTimePicker
            label="From"
            ampm={false}
            value={hasSpan ? new Date(visStartMs) : null}
            minDateTime={hasSpan ? new Date(workspaceStartMs) : undefined}
            maxDateTime={hasSpan ? new Date(visEndMs - minWin * 1000) : undefined}
            onChange={handleFromChange}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          {live ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.9,
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.success.main}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                whiteSpace: 'nowrap',
              }}
            >
              <BoltIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="caption" fontWeight={700} color="success.main">
                To · now (live)
              </Typography>
            </Box>
          ) : (
            <DateTimePicker
              label="To"
              ampm={false}
              value={hasSpan ? new Date(visEndMs) : null}
              minDateTime={hasSpan ? new Date(visStartMs + minWin * 1000) : undefined}
              maxDateTime={hasSpan ? new Date(workspaceEndMs) : undefined}
              onChange={handleToChange}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          )}
        </Stack>
      </LocalizationProvider>

      {/* Window length + presets */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.5 }}>
        <Chip
          size="small"
          label={`Window: ${formatDuration(clampedTail)}`}
          sx={{ height: 22, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.14), color: 'text.primary' }}
        />
        {availablePresets.map((p) => (
          <Chip
            key={p.label}
            size="small"
            label={p.label}
            variant={isActivePreset(p) ? 'filled' : 'outlined'}
            color={isActivePreset(p) ? 'primary' : 'default'}
            onClick={() => applyPreset(p)}
            sx={{ height: 22, fontSize: '0.68rem', cursor: 'pointer' }}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default RangePicker;
